'use client'

import * as React from 'react'
import { addDoc, collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Progress } from 'components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const BUDGET_COLLECTION = 'budgetCategories'
const EXPENDITURES_COLLECTION = 'expenditures'
const AUDIT_COLLECTION = 'auditLogs'

function formatCurrency(value, currency = 'KES') {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function ProjectBudgetPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [projectBudgetItems, setProjectBudgetItems] = React.useState(
    Array.isArray(project.budgetItems) ? project.budgetItems : [],
  )
  const [categories, setCategories] = React.useState([])
  const [expenditures, setExpenditures] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)

  const [categoryOpen, setCategoryOpen] = React.useState(false)
  const [editingCategory, setEditingCategory] = React.useState(null)
  const [categoryForm, setCategoryForm] = React.useState({
    category: '',
    approved: '',
  })

  const [expenseOpen, setExpenseOpen] = React.useState(false)
  const [expenseForm, setExpenseForm] = React.useState({
    date: '',
    category: '',
    description: '',
    amount: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadBudgetData() {
      try {
        const [categorySnap, expenseSnap] = await Promise.all([
          getDocs(query(collection(db, BUDGET_COLLECTION), where('projectId', '==', project.id))),
          getDocs(query(collection(db, EXPENDITURES_COLLECTION), where('projectId', '==', project.id))),
        ])
        if (!isMounted) return
        const fetchedCategories = categorySnap.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
          source: 'firestore',
        }))
        if (fetchedCategories.length === 0) {
          const fallback = (Array.isArray(projectBudgetItems) ? projectBudgetItems : []).map((item, index) => ({
            id: item.id || `fallback-${index}`,
            category: item.category || 'Other',
            approved: Number(item.total || 0),
            source: 'project',
            fallbackIndex: index,
          }))
          setCategories(fallback)
        } else {
          setCategories(fetchedCategories)
        }
        setExpenditures(expenseSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setCategories([])
        setExpenditures([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadBudgetData()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const spendByCategory = expenditures.reduce((acc, exp) => {
    const key = exp.category || 'Other'
    acc[key] = (acc[key] || 0) + Number(exp.amount || 0)
    return acc
  }, {})

  const budgetCategories = categories
  const selectableCategories = categories

  const normalizedCategories = budgetCategories.map((cat) => {
    const approved = Number(cat.approved || cat.total || 0)
    const spent = spendByCategory[cat.category] || Number(cat.spent || 0)
    const balance = approved - spent
    const percentUsed = approved > 0 ? (spent / approved) * 100 : 0
    return {
      ...cat,
      approved,
      spent,
      balance,
      percentUsed,
    }
  })

  const totalApproved = normalizedCategories.length > 0
    ? normalizedCategories.reduce((sum, cat) => sum + cat.approved, 0)
    : Number.isFinite(project.totalBudget)
      ? project.totalBudget
      : 0
  const totalSpent = expenditures.reduce((sum, exp) => sum + Number(exp.amount || 0), 0)
  const remaining = totalApproved - totalSpent
  const burnRate = totalApproved > 0 ? (totalSpent / totalApproved) * 100 : 0

  const chartData = normalizedCategories.map((cat) => ({
    category: cat.category,
    Approved: cat.approved,
    Spent: cat.spent,
  }))

  const resetCategoryForm = () => {
    setCategoryForm({ category: '', approved: '' })
    setEditingCategory(null)
  }

  const handleSaveCategory = async () => {
    if (!categoryForm.category.trim()) {
      toast.error('Please enter a budget category.')
      return
    }
    const approved = Number(categoryForm.approved || 0)
    if (!Number.isFinite(approved) || approved <= 0) {
      toast.error('Please enter a valid approved amount.')
      return
    }
    try {
      if (editingCategory) {
        if (editingCategory.source === 'project') {
          const index = Number(editingCategory.fallbackIndex || 0)
          const updatedBudgetItems = [...projectBudgetItems]
          const current = updatedBudgetItems[index] || {}
          const quantity = Number(current.quantity || 0)
          const updated = {
            ...current,
            category: categoryForm.category.trim(),
            total: approved,
            unitCost: quantity > 0 ? approved / quantity : current.unitCost,
          }
          updatedBudgetItems[index] = updated
          await updateDoc(doc(db, 'projects', project.id), {
            budgetItems: updatedBudgetItems,
            updatedAt: serverTimestamp(),
          })
          setProjectBudgetItems(updatedBudgetItems)
          setCategories((prev) =>
            prev.map((cat) =>
              cat.id === editingCategory.id
                ? { ...cat, category: categoryForm.category.trim(), approved }
                : cat,
            ),
          )
          await addDoc(collection(db, AUDIT_COLLECTION), {
            timestamp: serverTimestamp(),
            userId: auth.currentUser?.uid || 'system',
            userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
            action: 'UPDATE',
            entity: 'Project',
            entityId: project.id,
            changes: {
              before: '',
              after: `Updated budget item "${categoryForm.category.trim()}"`,
            },
          })
        } else {
          await updateDoc(doc(db, BUDGET_COLLECTION, editingCategory.id), {
            category: categoryForm.category.trim(),
            approved,
            updatedAt: serverTimestamp(),
          })
          setCategories((prev) =>
            prev.map((cat) =>
              cat.id === editingCategory.id
                ? { ...cat, category: categoryForm.category.trim(), approved }
                : cat,
            ),
          )
          await addDoc(collection(db, AUDIT_COLLECTION), {
            timestamp: serverTimestamp(),
            userId: auth.currentUser?.uid || 'system',
            userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
            action: 'UPDATE',
            entity: 'Project',
            entityId: project.id,
            changes: {
              before: '',
              after: `Updated budget category "${categoryForm.category.trim()}"`,
            },
          })
        }
      } else {
        const payload = {
          projectId: project.id,
          category: categoryForm.category.trim(),
          approved,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
        const docRef = await addDoc(collection(db, BUDGET_COLLECTION), payload)
        setCategories((prev) => [{ id: docRef.id, ...payload, source: 'firestore' }, ...prev])
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: auth.currentUser?.uid || 'system',
          userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added budget category "${payload.category}"`,
          },
        })
      }
      toast.success(editingCategory ? 'Category updated.' : 'Category added.')
      resetCategoryForm()
      setCategoryOpen(false)
    } catch (error) {
      toast.error('Failed to save budget category', {
        description: error?.message || 'Please try again.',
      })
    }
  }

  const openEditCategory = (category) => {
    setEditingCategory(category)
    setCategoryForm({
      category: category.category || '',
      approved: String(category.approved || category.total || ''),
    })
    setCategoryOpen(true)
  }

  const resetExpenseForm = () => {
    setExpenseForm({ date: '', category: '', description: '', amount: '' })
  }

  const handleSaveExpense = async () => {
    if (!expenseForm.category.trim() || !expenseForm.amount) {
      toast.error('Please select a category and enter an amount.')
      return
    }
    const amount = Number(expenseForm.amount)
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Please enter a valid amount.')
      return
    }
    try {
      const payload = {
        projectId: project.id,
        category: expenseForm.category.trim(),
        description: expenseForm.description.trim(),
        amount,
        date: expenseForm.date || null,
        submittedBy: auth.currentUser?.uid || 'system',
        submittedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, EXPENDITURES_COLLECTION), payload)
      setExpenditures((prev) => [{ id: docRef.id, ...payload }, ...prev])
      await addDoc(collection(db, AUDIT_COLLECTION), {
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'system',
        userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
        action: 'CREATE',
        entity: 'Project',
        entityId: project.id,
        changes: {
          before: '',
          after: `Added expenditure "${payload.description || payload.category}"`,
        },
      })
      toast.success('Expenditure added.')
      resetExpenseForm()
      setExpenseOpen(false)
    } catch (error) {
      toast.error('Failed to add expenditure', {
        description: error?.message || 'Please try again.',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Total Budget', value: formatCurrency(totalApproved, project.currency), desc: project.currency || 'KES' },
          { label: 'Total Spent', value: formatCurrency(totalSpent, project.currency), desc: `${burnRate.toFixed(1)}% utilized` },
          { label: 'Remaining', value: formatCurrency(remaining, project.currency), desc: `${(100 - burnRate).toFixed(1)}% available` },
          { label: 'Burn Rate', value: `${burnRate.toFixed(1)}%`, desc: burnRate > 80 ? 'High utilization' : 'On track' },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className="text-xl font-bold text-foreground mt-1">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Budget vs Actual</CardTitle>
          <CardDescription>Approved budget compared to actual expenditure by category</CardDescription>
        </CardHeader>
        <CardContent>
          {normalizedCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground">No budget categories yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="category" stroke="var(--color-muted-foreground)" fontSize={11} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={11} />
                <Tooltip
                  formatter={(value) => formatCurrency(value, project.currency)}
                  contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                />
                <Legend />
                <Bar dataKey="Approved" fill="var(--color-chart-1)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Spent" fill="var(--color-chart-2)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Budget Breakdown</CardTitle>
            <CardDescription>Manage approved budget categories and utilization.</CardDescription>
          </div>
          {canEdit ? (
            <Dialog open={categoryOpen} onOpenChange={(open) => { setCategoryOpen(open); if (!open) resetCategoryForm() }}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => {
                    resetCategoryForm()
                    setCategoryOpen(true)
                  }}
                >
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Edit Budget Category' : 'Add Budget Category'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    value={categoryForm.category}
                    onChange={(event) => setCategoryForm((prev) => ({ ...prev, category: event.target.value }))}
                    placeholder="Personnel, Equipment, Travel"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Approved Amount</Label>
                  <Input
                    type="number"
                    value={categoryForm.approved}
                    onChange={(event) => setCategoryForm((prev) => ({ ...prev, approved: event.target.value }))}
                    placeholder="0"
                  />
                </div>
                <Button className="w-full" onClick={handleSaveCategory}>
                  {editingCategory ? 'Update Category' : 'Save Category'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Approved</TableHead>
                <TableHead className="text-right">Spent</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">% Used</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-20">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    Loading budget data...
                  </TableCell>
                </TableRow>
              ) : normalizedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    No budget categories yet.
                  </TableCell>
                </TableRow>
              ) : (
                normalizedCategories.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.category || 'Other'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.approved, project.currency)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.spent, project.currency)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.balance, project.currency)}</TableCell>
                    <TableCell className="text-right font-medium">{item.percentUsed.toFixed(1)}%</TableCell>
                    <TableCell>
                      <Progress
                        value={item.percentUsed}
                        className={`h-2 ${item.percentUsed >= 90 ? '[&>div]:bg-red-500' : item.percentUsed >= 70 ? '[&>div]:bg-amber-500' : ''}`}
                      />
                    </TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Button variant="ghost" size="sm" onClick={() => openEditCategory(item)}>
                          Edit
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Expenditure Log</CardTitle>
            <CardDescription>Track actual spending against approved budgets.</CardDescription>
          </div>
          {canEdit ? (
            <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
              <DialogTrigger asChild>
                <Button size="sm">Add Expenditure</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Expenditure</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={expenseForm.date}
                    onChange={(event) => setExpenseForm((prev) => ({ ...prev, date: event.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  {selectableCategories.length === 0 ? (
                    <Input
                      value={expenseForm.category}
                      onChange={(event) => setExpenseForm((prev) => ({ ...prev, category: event.target.value }))}
                      placeholder="Enter category"
                    />
                  ) : (
                    <Select
                      value={expenseForm.category}
                      onValueChange={(value) => setExpenseForm((prev) => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.category}>
                            {cat.category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={expenseForm.description}
                    onChange={(event) => setExpenseForm((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Brief description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={expenseForm.amount}
                    onChange={(event) => setExpenseForm((prev) => ({ ...prev, amount: event.target.value }))}
                    placeholder="0"
                  />
                </div>
                <Button className="w-full" onClick={handleSaveExpense}>
                  Save Expenditure
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Submitted By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                    Loading expenditures...
                  </TableCell>
                </TableRow>
              ) : expenditures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                    No expenditures recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                expenditures.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="text-sm">{formatDate(exp.date || exp.createdAt)}</TableCell>
                    <TableCell>{exp.category || '—'}</TableCell>
                    <TableCell>{exp.description || '—'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(exp.amount, project.currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {exp.submittedByName || exp.submittedBy || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
